import type { AlertAction, ChangeOrderStatus, Detection } from '../enums.js';

// Copy the web app composes from API data, ported from the prototype's script so the UI
// holds no wording or thresholds of its own (CLAUDE.md section 7, ADR 0016).

/** Button labels for alert actions outside the recommendation (spec §4). */
export const ALERT_ACTION_LABELS = {
  'accept-recommendation': 'Follow up',
  'mark-fixed': 'I’ve fixed it',
  'accept-deviation': 'Accept as intentional',
  reassign: 'Reassign',
  recheck: 'Re-check now',
  close: 'Close alert',
  escalate: 'Escalate to manager',
} as const satisfies Record<AlertAction, string>;

/** "ECO-2291 · Approved" in the evidence row. */
export const CHANGE_ORDER_STATUS_LABELS = {
  approved: 'Approved',
  in_review: 'In review',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
} as const satisfies Record<ChangeOrderStatus, string>;

/** Prototype `alertTitle`: the part description plus what differs. */
const HEADING_SUFFIX = {
  quantity: 'quantity differs',
  configuration: 'configuration differs',
  presence_missing_in_erp: 'in the design, missing in ERP',
  presence_extra_in_erp: 'in the ERP, not in the design',
  assembly_not_in_erp: 'assembly not in ERP',
} as const satisfies Record<Detection, string>;

export function alertHeading(title: string, detection: Detection): string {
  return `${title}: ${HEADING_SUFFIX[detection]}`;
}

/** Prototype `spell`: "Three parts need a decision." */
export function spellCount(n: number): string {
  return (
    ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'][n] ?? String(n)
  );
}

export interface HeroInput {
  openCount: number;
  waitingCount: number;
  /** The "Fix first" rows, already sorted by the API (soonest PO, then severity). */
  fixFirst: readonly { description: string; poCode: string | null; poDays: number | null }[];
}

/** Prototype `ffHead` plus the "N waiting on someone else." suffix of `vDash`. */
export function dashboardHero({ openCount, waitingCount, fixFirst }: HeroInput): {
  heading: string;
  sub: string;
} {
  const waiting = waitingCount ? ` ${String(waitingCount)} waiting on someone else.` : '';
  const n = fixFirst.length;
  if (!n) {
    return {
      heading: 'Nothing needs you right now.',
      sub:
        (openCount
          ? 'Baseline is waiting on replies and will re-check on its own.'
          : 'PDM and ERP agree on every line.') + waiting,
    };
  }
  const top = fixFirst.find((r) => r.poCode !== null) ?? fixFirst[0];
  const sub =
    top?.poCode && top.poDays !== null
      ? `${top.description} is the most urgent: ${top.poCode} releases in ${String(top.poDays)} days.`
      : `${top?.description ?? ''} has been open longest.`;
  return {
    heading: `${spellCount(n)} ${n === 1 ? 'part needs' : 'parts need'} a decision.`,
    sub: sub + waiting,
  };
}

/** Prototype PO chip colour: 3 days or less is urgent, 7 or less is soon, else far. */
export type PoUrgency = 'urgent' | 'soon' | 'far';
export function poUrgency(days: number): PoUrgency {
  if (days <= 3) return 'urgent';
  if (days <= 7) return 'soon';
  return 'far';
}

/** Width of a "Purchase orders releasing soon" bar, out of the 14-day window. */
export const PO_WINDOW_DAYS = 14;
export function poBarPercent(days: number): number {
  return Math.max(6, ((PO_WINDOW_DAYS - days) / PO_WINDOW_DAYS) * 100);
}

const DAY_MS = 86_400_000;

function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Calendar days from `from` to `to`, in the viewer's time zone. */
export function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((localMidnight(to) - localMidnight(from)) / DAY_MS);
}

/** The prototype's Age column: "Today", "1 day", "3 days". */
export function ageLabel(openedAt: string, now: Date = new Date()): string {
  const days = Math.max(0, calendarDaysBetween(new Date(openedAt), now));
  if (days === 0) return 'Today';
  return `${String(days)} ${days === 1 ? 'day' : 'days'}`;
}

/** Days until a `YYYY-MM-DD` release date. */
export function daysUntil(isoDate: string, now: Date = new Date()): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  return calendarDaysBetween(now, new Date(y ?? 0, (m ?? 1) - 1, d ?? 1));
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');

/** Prototype timestamps in the viewer's time zone: "Mon 28 Sep, 06:00". */
export function formatStamp(iso: string): string {
  const d = new Date(iso);
  return `${WEEKDAYS[d.getDay()] ?? ''} ${String(d.getDate())} ${MONTHS[d.getMonth()] ?? ''}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Compare delta: "+2", "−2" (a real minus sign) or "—". */
export function formatDelta(delta: number): string {
  if (delta === 0) return '—';
  return `${delta > 0 ? '+' : '−'}${String(Math.abs(delta))}`;
}

/** The waiting panel: "Waiting for Jonas Weber (Change analyst)" or the supply team. */
export function waitingHeadline(
  request: { requestedName: string; requestedRole: string } | null,
): string {
  return request
    ? `Waiting for ${request.requestedName} (${request.requestedRole})`
    : 'Waiting for a reply from the supply team';
}

/** The recommendation card's closing line for an alert that is no longer open. */
export function closedLine(
  state: 'closed_verified' | 'closed_human' | 'accepted_deviation' | 'superseded',
  reason: string | null,
): { bold: string; rest: string } {
  switch (state) {
    case 'closed_verified':
      return { bold: 'Closed as verified.', rest: 'PDM and ERP now match.' };
    case 'closed_human':
      return { bold: 'Closed.', rest: reason ?? '' };
    case 'accepted_deviation':
      return { bold: 'Accepted as intentional.', rest: 'Excluded from open alerts.' };
    case 'superseded':
      return { bold: 'Superseded.', rest: 'A newer alert covers this line.' };
  }
}
